import { ChatBubbleLeftIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { ComponentPropsWithoutRef, ComponentType, useMemo } from 'react';

import { useTranslation } from '@/hooks/useTranslation';

import { Badge } from '../../core/Badge';
import { Icon } from '../../core/Icon';
import HoverPopover from '../hoverPopover';
import { TooltipItem, TooltipList, TooltipSeverity } from './actionTooltipList';

const extractReviewMessage = (entry: string) => entry.split(':')[3] ?? '';

type ReviewIcon = ComponentType<ComponentPropsWithoutRef<'svg'> & { title?: string }>;
type ReviewIconVariant = 'destructive' | 'success' | 'warning' | 'lighter-gray';
type ReviewColorScheme = 'normal' | 'light';

type ReviewVisual = {
    icon: ReviewIcon;
    variant: ReviewIconVariant;
    colorScheme?: ReviewColorScheme;
};

/** Mirrors legacy getReviewClass() status → colour/icon mapping. */
const getReviewVisual = (item: any): ReviewVisual => {
    if (item.reviewCountTotal__ === item.reviewCountReceived__) {
        return { icon: ChatBubbleLeftIcon, variant: 'lighter-gray' };
    }

    if (item.reviewCountReviewed__ > 0 && item.reviewCountApproved__ > 0) {
        return { icon: ChatBubbleLeftRightIcon, variant: 'warning' };
    }

    if (item.reviewCountReviewed__ > 0) {
        return { icon: ChatBubbleLeftIcon, variant: 'destructive' };
    }

    if (item.reviewCountApproved__ > 0) {
        return { icon: ChatBubbleLeftIcon, variant: 'success' };
    }

    return { icon: ChatBubbleLeftIcon, variant: 'warning', colorScheme: 'light' };
};

export const ReviewPopover = ({
    item,
    showItemReview,
}: {
    item: any;
    showItemReview?: (item: any) => void;
}) => {
    const { t } = useTranslation();

    const items = useMemo(() => {
        const toItems = (entries: string[] | undefined, severity: TooltipSeverity): TooltipItem[] => {
            const result: TooltipItem[] = [];
            (entries || []).forEach((entry, index) => {
                const text = extractReviewMessage(entry);
                if (text) {
                    result.push({ key: `${severity}-${index}-${entry}`, text, severity });
                }
            });
            return result;
        };

        return [
            ...toItems(item.reviewErrors__, 'error'),
            ...toItems(item.reviewWarnings__, 'warning'),
            ...toItems(item.reviewRemarks__, 'remark'),
        ];
    }, [item.reviewErrors__, item.reviewWarnings__, item.reviewRemarks__]);

    const visual = useMemo(() => getReviewVisual(item), [
        item.reviewCountTotal__,
        item.reviewCountReceived__,
        item.reviewCountReviewed__,
        item.reviewCountApproved__,
    ]);

    const errorCount = item.reviewErrors__?.length ?? 0;
    const warningCount = item.reviewWarnings__?.length ?? 0;
    const hasBadges = errorCount > 0 || warningCount > 0;

    const trigger = (
        <div className="tw-relative">
            <button
                type="button"
                className={`glyphicon
                    tw-flex
                    tw-items-center
                    tw-justify-center
                    tw-cursor-pointer
                    tw-bg-transparent
                    icon-button
                    focus-visible:!tw-border-primary-focus
                    tw-h-10
                    ${hasBadges ? 'tw-pr-[15px]' : ''}`}
                onClick={() => showItemReview?.(item)}
                title={t('ITEMS_LIST_ACTION_VIEW_FEEDBACK')}
            >
                <Icon
                    icon={visual.icon}
                    size="md"
                    variant={visual.variant}
                    colorScheme={visual.colorScheme ?? 'normal'}
                />
            </button>
            {errorCount > 0 && (
                <Badge variant="error" type="count" className="tw-absolute tw-top-1 tw-right-0">
                    {errorCount}
                </Badge>
            )}
            {warningCount > 0 && (
                <Badge variant="warning" type="count" className="tw-absolute tw-bottom-1 tw-right-0">
                    {warningCount}
                </Badge>
            )}
        </div>
    );

    if (!items.length) {
        return trigger;
    }

    return <HoverPopover triggerContent={trigger}><TooltipList items={items} /></HoverPopover>;
};
